<!-- Refer to the following document for the logic to display a list-menu or overflow-menu
     with responsive guidelines
     https://www.figma.com/file/3fYJZJe6DCMilO28qLWY7k/SLS---Data-Table
-->
<template>
  <component
    v-bind="currentProperties"
    :is="tagType"
    ref="selectionBarMenu"
    class="selection-bar-menu"
    :class="rootClasses"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
import { isDefined } from "@utils/var.check";
import { ref, computed, onMounted, useSlots, useAttrs, useTemplateRef, onUnmounted } from "vue";
import { useVm } from "@utils/vm.ts";

// Props
interface Props {
  type?: string;
}

const { type = "list-menu" } = defineProps<Props>();

// Composables
const { vm } = useVm();
const slots = useSlots();
const attrs = useAttrs();

// Data
const moreThanOneItem = ref(false);
const moreThanThreeItems = ref(false);
const observer = ref<MutationObserver | null>(null);

// Template refs
const selectionBarMenu = useTemplateRef<HTMLElement>("selectionBarMenu");

// Computed
const rootClasses = computed(() => {
  return {
    "list-menu": isListMenu.value,
    "overflow-menu": isOverflowMenu.value,
    "more-than-one-item": moreThanOneItem.value,
    "more-than-three-items": moreThanThreeItems.value,
  };
});

const isListMenu = computed(() => {
  return type === "list-menu";
});

const isOverflowMenu = computed(() => {
  return type === "overflow-menu";
});

const tagType = computed(() => {
  return isOverflowMenu.value ? "cv-overflow-menu" : "ul";
});

const currentProperties = computed(() => {
  return isOverflowMenu.value
    ? {
        flipMenu: true,
      }
    : attrs;
});

// Methods
const checkDefinedSlotsMoreThan = (num: number): boolean => {
  if (slots.default) {
    const definedSlots = slots
      .default?.()
      .filter((slot) => isDefined(slot.type) && typeof slot.type === "object");
    return definedSlots.length > num;
  }
  return false;
};

const updateSlotCounts = () => {
  moreThanOneItem.value = checkDefinedSlotsMoreThan(1);
  moreThanThreeItems.value = checkDefinedSlotsMoreThan(3);
};

// Lifecycle hooks
onMounted(() => {
  observer.value = new MutationObserver(() => {
    updateSlotCounts();
  });

  if (type === "list-menu") {
    if (vm.$el) {
      observer.value.observe(vm.$el, { subtree: true, childList: true });
    }
  } else {
    // Handle overflow menu case
    const menuElement = selectionBarMenu.value;
    if (menuElement) {
      const selectionBarMenuId = menuElement.$el.getAttribute("id");
      const menu = document.getElementById(`${selectionBarMenuId}-menu`);
      if (menu) {
        observer.value.observe(menu, { subtree: true, childList: true });
      }
    }
  }

  updateSlotCounts();
});

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect();
  }
});
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/abstracts/variables";
@import "@styles/themes/sls-theme/tokens/colors";

.selection-bar-menu {
  @include media("mobile") {
    &.list-menu {
      &.more-than-one-item {
        display: none !important;
      }
    }
    &.overflow-menu {
      &:not(.more-than-one-item) {
        display: none !important;
      }
    }
  }
  @include media("tablet-min") {
    &.list-menu {
      &.more-than-three-items {
        display: none !important;
      }
    }
    &.overflow-menu {
      &:not(.more-than-three-items) {
        display: none !important;
      }
    }
  }

  &.bx--overflow-menu {
    &:hover {
      background-color: $inverse-hover-ui;
    }

    &:focus,
    &:active {
      background-color: $inverse-focus-ui;
    }

    .bx--overflow-menu__trigger {
      &.bx--tooltip__trigger {
        svg {
          width: 24px;
          height: 24px;
          fill: $inverse-link;
        }
      }
    }
  }
}
</style>
